import React, { useCallback, useMemo } from 'react';
import {
  AccordionSummary,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import makeStyles from '@mui/styles/makeStyles';
import RemoveCircleOutlineSharpIcon from '@mui/icons-material/RemoveCircleOutlineSharp';
import { DateTime } from 'luxon';
import { useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import CutIcon from '@mui/icons-material/ContentCut';
import { useDialogState } from 'modules/common/hooks';
import { useMessages } from 'modules/intl';
import { TimeSignal } from 'graphql/types';
import { NoteSources } from 'modules/notes/list/NoteSources';
import { NoteTimeDuration } from '../NoteTimeDuration';
import { SplitEntry, SPLIT_NOTE_FIELDS } from '../../types';
import { useSplitEntriesContext } from '../../SplitEntriesProvider';
import { RemoveEntryDialog } from '../RemoveEntryDialog';
import { useDurationSplitBarHoveredEntryContext } from '../DurationSplitBar/DurationSplitBarHoveredEntryProvider';

type SplitEntryAccordionSummaryProps = {
  note: TimeSignal;
  entry: SplitEntry;
  expanded: boolean;
  enableSplit: boolean;
  position: number;
};

type AccordionStyleArgs = {
  isHoveredEntry: boolean;
};

const useStyles = makeStyles((theme) => ({
  title: {
    ...theme.typography.body1,
    color: theme.palette.text.secondary,
    paddingRight: theme.spacing(1),
    alignSelf: 'center'
  },
  titleContainer: {
    display: 'flex'
  },
  cutIcon: {
    color: theme.palette.common.white,
    transform: 'rotate(-90deg)',
    padding: theme.spacing(0.375)
  },
  cutIconBtn: {
    fill: theme.palette.common.white,
    width: theme.spacing(3.5),
    height: theme.spacing(3.5),
    padding: theme.spacing(0.75),
    marginRight: theme.spacing(1),
    background: theme.palette.primary.main,
    '&:hover': {
      background: theme.palette.primary.main
    }
  },
  removeIcon: {
    color: theme.palette.secondary.main,
    padding: theme.spacing(1)
  }
}));

const useTimeStyles = makeStyles((theme) => ({
  root: { display: 'flex', alignItems: 'baseline' },
  duration: {
    padding: theme.spacing(0, 3, 0, 4)
  }
}));

const useAccordianStyles = makeStyles((theme) => ({
  root: {
    minHeight: `${theme.spacing(6)} !important`,
    border: ({ isHoveredEntry }: AccordionStyleArgs) =>
      isHoveredEntry
        ? `1px solid ${theme.palette.primary.main}`
        : '1px solid transparent',
    borderRadius: theme.spacing(0.5)
  },
  content: {
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '8px 0px !important'
  },
  expandIcon: {
    color: theme.palette.text.primary
  }
}));

const messageIds = ['note.split.split', 'note.split.remove'];
export const SplitEntryAccordionSummary: React.FC<SplitEntryAccordionSummaryProps> = ({
  note,
  entry,
  expanded,
  enableSplit,
  position
}: SplitEntryAccordionSummaryProps) => {
  const { splitEntryHandler, removeSplitHandler } = useSplitEntriesContext();
  const { hoveredEntryId } = useDurationSplitBarHoveredEntryContext();

  const classes = useStyles();
  const timeClasses = useTimeStyles();
  const accordianStyleClasses = useAccordianStyles({
    isHoveredEntry: entry.id === hoveredEntryId
  });
  const messages = useMessages(messageIds);

  const tooltipTitle = useMemo(
    () =>
      expanded ? (
        <FormattedMessage id="collapse" />
      ) : (
        <FormattedMessage id="expand" />
      ),
    [expanded]
  );

  const expandIcon = useMemo(
    () => (
      <Tooltip title={tooltipTitle}>
        <ExpandMoreIcon color="inherit" />
      </Tooltip>
    ),
    [tooltipTitle]
  );

  const onCutClick = useCallback(
    (e) => {
      e.stopPropagation();
      splitEntryHandler(entry.id);
    },
    [entry.id, splitEntryHandler]
  );

  const onDeleteClick = useCallback(() => {
    removeSplitHandler(entry.id);
  }, [entry.id, removeSplitHandler]);
  const startTime = useWatch({
    name: `${SPLIT_NOTE_FIELDS.SPLIT_ENTRIES}.${position}.${SPLIT_NOTE_FIELDS.START_TIME}`,
    defaultValue: entry.startTime
  });
  const endTime = useWatch({
    name: `${SPLIT_NOTE_FIELDS.SPLIT_ENTRIES}.${position}.${SPLIT_NOTE_FIELDS.END_TIME}`,
    defaultValue: entry.endTime
  });
  const { open, openDialog, closeDialog } = useDialogState();
  const onRemoveClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      openDialog();
    },
    [openDialog]
  );

  return (
    <>
      <AccordionSummary
        expandIcon={expandIcon}
        classes={accordianStyleClasses}
        data-testid={`accordian_summary_${entry.id}`}
      >
        <div className={classes.titleContainer}>
          <Typography variant="body1" className={classes.title}>
            {entry.name}
          </Typography>
          {note.sources && !expanded && (
            <NoteSources maxAvatar={5} sources={note.sources} />
          )}
        </div>
        {expanded ? (
          <div>
            {enableSplit && (
              <Tooltip title={messages[messageIds[0]]}>
                <IconButton
                  className={classes.cutIconBtn}
                  onClick={onCutClick}
                  data-testid={`accordian_cut_icon_${entry.id}`}
                  size="large"
                >
                  <CutIcon className={classes.cutIcon} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={messages[messageIds[1]]}>
              <IconButton
                className={classes.removeIcon}
                onClick={onRemoveClick}
                data-testid={`accordian_remove_icon_${entry.id}`}
                size="large"
              >
                <RemoveCircleOutlineSharpIcon />
              </IconButton>
            </Tooltip>
          </div>
        ) : (
          <NoteTimeDuration
            testId={`accordian_time_${entry.id}`}
            classes={timeClasses}
            startTime={
              startTime ? DateTime.fromJSDate(startTime).toISO() : undefined
            }
            endTime={endTime ? DateTime.fromJSDate(endTime).toISO() : undefined}
            duration={entry.duration}
          />
        )}
      </AccordionSummary>
      {open && (
        <RemoveEntryDialog
          open={open}
          removeEntry={onDeleteClick}
          closeHandler={closeDialog}
        />
      )}
    </>
  );
};
